import { useState, useCallback } from 'react';
import type { Marker } from '@googlemaps/markerclusterer';

/**
 * Hook for managing marker references on a map.
 * Tracks markers by their unique identifier and provides a callback for adding/removing markers.
 *
 * @returns An object containing the markers collection and functions to manage it
 */
export function useMarkerRefs<T extends Marker = Marker>() {
  const [markers, setMarkers] = useState<Record<string, T>>({});

  /**
   * Callback to set or remove a marker reference
   *
   * @param marker The marker instance or null when removing
   * @param markerId Unique identifier for the marker (e.g. locationId)
   */
  const setMarkerRef = useCallback((marker: T | null, markerId: string) => {
    setMarkers(markers => {
      // Skip update if there's no actual change to the markers collection
      if ((marker && markers[markerId]) || (!marker && !markers[markerId])) {
        return markers;
      }

      if (marker) {
        // Add marker to the collection
        return { ...markers, [markerId]: marker };
      } else {
        // Remove marker from the collection
        const { [markerId]: _, ...newMarkers } = markers;
        return newMarkers;
      }
    });
  }, []);

  return {
    markers,
    setMarkerRef
  };
}